import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OrderState } from '@erpgold/shared';
import { orderKeys } from '@/services/queryKeys';
import { orderService } from '@/services/order.service';
import type { TenantOrder } from '@/mocks/orders';
import { USE_MOCK } from '@/utils/env';

async function mockDelay<T>(value: T, ms = 200): Promise<T> {
  await new Promise((r) => setTimeout(r, ms));
  return value;
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      USE_MOCK
        ? mockDelay({ _id: id, state: OrderState.CANCELED, note: reason } as unknown as TenantOrder)
        : orderService.cancel(id, reason),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useConfirmOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      USE_MOCK
        ? mockDelay({ _id: id, state: OrderState.CONFIRMED } as unknown as TenantOrder)
        : orderService.confirm(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(id) });
    },
  });
}

export function useShipOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, tracking_number, carrier }: { id: string; tracking_number: string; carrier: string }) =>
      USE_MOCK
        ? mockDelay({ _id: id, state: OrderState.DELIVERING } as unknown as TenantOrder)
        : orderService.ship(id, tracking_number, carrier),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useCompleteOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      USE_MOCK
        ? mockDelay({ _id: id, state: OrderState.COMPLETED } as unknown as TenantOrder)
        : orderService.complete(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(id) });
    },
  });
}

export function useUpdateOrderNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) =>
      USE_MOCK
        ? mockDelay({ _id: id, note } as unknown as TenantOrder)
        : orderService.update(id, { notes: note }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, branch_id, branch_name, branch_address }: { id: string; branch_id: string; branch_name: string; branch_address: string }) =>
      USE_MOCK
        ? mockDelay({
            _id: id,
            store: { _id: branch_id, name: branch_name, address: branch_address },
          } as unknown as TenantOrder)
        : orderService.update(id, { store_id: branch_id }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, staff_id, staff_name }: { id: string; staff_id: string; staff_name: string }) =>
      USE_MOCK
        ? mockDelay({
            _id: id,
            cashier: { name: staff_name },
          } as unknown as TenantOrder)
        : orderService.update(id, { sales_person_id: staff_id }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderWarehouseStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, item_sku, staff_id, staff_name: _staff_name }: { id: string; item_sku: string; staff_id: string; staff_name: string }) =>
      USE_MOCK
        ? mockDelay({
            _id: id,
          } as unknown as TenantOrder)
        : orderService.update(id, { items: [{ sku: item_sku, warehouse_staff_id: staff_id }] }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, customer_id }: { id: string; customer_id: string }) =>
      orderService.updateCustomer(id, customer_id),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderShipping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, shipping }: { id: string; shipping: { name: string; phone: string; address: string; province?: string; citizen_id?: string; delivery_days?: number; expected_delivery_date?: string; delivery_method?: string; email?: string; ward?: string; branch_id?: string } }) => {
      const payload = {
        store_id: shipping.branch_id || undefined,
        shipping: {
          address: {
            name: shipping.name,
            phone: shipping.phone,
            address: shipping.address,
            province_id: shipping.province,
            ward_id: shipping.ward,
            email: shipping.email,
            citizen_id: shipping.citizen_id,
          },
          method: shipping.delivery_method,
          estimated_days: shipping.delivery_days,
          estimated_delivery: shipping.expected_delivery_date ? { from: shipping.expected_delivery_date, to: shipping.expected_delivery_date } : undefined,
        }
      };
      return USE_MOCK
        ? mockDelay({
            _id: id,
            store: shipping.branch_id ? { _id: shipping.branch_id, name: 'Chi nhánh được chọn', address: '' } : undefined,
            ...payload,
          } as unknown as TenantOrder)
        : orderService.update(id, payload);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, invoice }: { id: string; invoice: { name?: string; tax_id?: string; address?: string; email?: string; buyer_name?: string; buyer_cccd?: string } }) =>
      USE_MOCK
        ? mockDelay({
            _id: id,
            buyer_invoice_info: invoice,
          } as unknown as TenantOrder)
        : orderService.update(id, { buyer_invoice_info: invoice }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useUpdateOrderItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, items }: { id: string; items: Array<{ sku: string; qty: number; price: number; price_method?: string; warehouse_name?: string; serial?: string }> }) =>
      USE_MOCK
        ? mockDelay({
            _id: id,
            items: items.map(item => ({
              sku: item.sku,
              product_name: 'Sản phẩm ' + item.sku,
              qty: item.qty,
              price: item.price,
              price_method: item.price_method,
              warehouse_name: item.warehouse_name,
              serial: item.serial
            })),
            total_amount: items.reduce((sum, item) => sum + item.qty * item.price, 0),
            subtotal: items.reduce((sum, item) => sum + item.qty * item.price, 0),
          } as unknown as TenantOrder)
        : orderService.update(id, { items }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useCreateOrderReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, receipt }: { id: string; receipt: { type: 'cash' | 'transfer' | 'split'; doc_date: string; posting_date: string; reason?: string; details: Array<{ description?: string; bank_name?: string; bank_account_debt?: string; bank_account_credit?: string; amount: number }> } }) =>
      USE_MOCK
        ? mockDelay({
            _id: id,
          } as unknown as TenantOrder)
        : orderService.createReceipt(id, receipt),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.id) });
    },
  });
}

export function useSyncOrderMisa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      USE_MOCK
        ? mockDelay({
            _id: id,
            misa_sync_at: new Date().toISOString(),
            misa_order_number: 'MS-' + Math.floor(100000 + Math.random() * 900000),
            misa_sale_voucher_code: 'CTBH-' + Math.floor(100000 + Math.random() * 900000),
            misa_export_voucher_code: 'XK-' + Math.floor(100000 + Math.random() * 900000),
          } as unknown as TenantOrder)
        : orderService.syncMisa(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(id) });
    },
  });
}

export function useRevealOrderSensitive() {
  return useMutation({
    mutationFn: async ({ id, password }: { id: string; password: string }) =>
      USE_MOCK
        ? mockDelay({ citizen_id: '012345678912', invoice_buyer_cccd: '012345678912' })
        : orderService.revealSensitive(id, password),
  });
}
